import React, { useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n';
import scaleSize from 'utils/scale-size';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { hideModal, showModal } from 'actions/modal';
import { MODAL_TYPES } from 'constants/modal-types';
import { hideLoader, showLoader } from 'actions/loader';
import apiCubic from 'config/api-cubic';
import requester from 'utils/requester';
import { AuthContext } from './AuthContext';
import Header from './common/Header';
import PhoneForm from './common/PhoneForm';
import ActionsSection from './ActionsSection';
import { StyledBlock, StyledLegalText } from './StyledComponents';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import { INIT_UI_LANGUAGE } from 'constants/languages';

const i18nScope = 'authorizationPage';

const SECTION_NAME = 'PHONE_NUMBER_INPUT';

const AUTH_TYPES = {
  registration: 'registration',
  authorization: 'authorization',
};

const SEVERAL_ACCOUNTS_ERRORS = ['universalauthorization_error_1', 'universalauthorization_error_2'];

const LOGIN_MODAL_OPTIONS = [
  {
    type: '/login/credentials',
    label: i18n.t(`${i18nScope}.loginCredentials`),
  },
  {
    type: '/login/qr',
    label: i18n.t(`${i18nScope}.loginQR`),
  },
];

const AuthorizationMainPage = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = history.location;
  const { contextState, setContextState } = useContext(AuthContext);
  const { code, phone } = contextState;

  const onEnterClick = useCallback(
    async ({ code, phone }) => {
      const authToken = apiCubic.getAuthToken();

      try {
        dispatch(showLoader());
        const result = await requester({
          method: 'post',
          url: `${apiCubic.host}${apiCubic.endpoints.universalAuthSendOTP}`,
          appendParams: false,
          extendHeadersWithSession: false,
          headers: { Authorization: `Basic ${authToken}` },
          data: { phone: `0${phone}`, language: INIT_UI_LANGUAGE },
        });

        const { data, error } = result;
        const { payload } = data || {};
        const userFlag = payload?.userFlag;

        setContextState({ code, phone, userFlag });

        if (!error) {
          innerAnalyticsVolia.postEvent({
            eventGroup: 'sign in screen',
            eventName: 'submit phone',
            eventValue: payload.type,
            addInfo: `0${phone}; ${userFlag}`,
          });

          if (payload.type === AUTH_TYPES.authorization) history.push(`${match.url}/login/otp${search}`);
          if (payload.type === AUTH_TYPES.registration) history.push(`${match.url}/registration/otp${search}`);
          return;
        }

        innerAnalyticsVolia.postEvent({
          eventGroup: 'sign in screen',
          eventName: 'submit phone',
          eventValue: error.code,
          addInfo: `0${phone}`,
        });

        if (SEVERAL_ACCOUNTS_ERRORS.includes(error.code)) {
          dispatch(
            showModal(MODAL_TYPES.LOGIN, {
              onModalBack() {
                dispatch(hideModal());
                SpatialNavigationWrapper.focus(`@${SECTION_NAME}`);
              },
              onModalConfirm(type) {
                dispatch(hideModal());
                innerAnalyticsVolia.postEvent({
                  eventGroup: 'sign in screen',
                  eventName: !type.includes('qr') ? 'click sign in with a other methods' : 'сlick login via рс or web',
                  eventValue: 'error pop up',
                  addInfo: `0${phone}`,
                });
                history.push(`${match.url}${type}${search}`);
              },
              title: i18n.t(`${i18nScope}.severalAccounts.title`),
              description: error.message || i18n.t(`${i18nScope}.severalAccounts.description`),
              rejectText: i18n.t('common.cancel'),
              options: LOGIN_MODAL_OPTIONS,
            }),
          );
          return;
        }

        if (error) throw new Error(error.message);
      } catch (error) {
        console.error(error);
        dispatch(
          showModal(MODAL_TYPES.INFO, {
            onModalBack() {
              dispatch(hideModal());
              SpatialNavigationWrapper.focus(`@${SECTION_NAME}`);
            },
            message: error.message,
          }),
        );
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, history, match.url, search, setContextState],
  );

  return (
    <>
      <Header title={i18n.t(`${i18nScope}.title`)} description={i18n.t(`${i18nScope}.description`)} />
      <PhoneForm
        sectionName={SECTION_NAME}
        code={code}
        initialValue={phone}
        buttonLabel={i18n.t(`${i18nScope}.login`)}
        invalidMessage={i18n.t(`${i18nScope}.phoneInvalid`)}
        onSubmit={onEnterClick}
      >
        <ActionsSection.Link
          label={i18n.t(`${i18nScope}.loginCredentials`)}
          to={`${match.url}/login/credentials${search}`}
          analyticsEventGroup={'sign in screen'}
          analyticsEventName={'click sign in with a other methods'}
          analyticsEventValue={'main screen'}
        />
        <ActionsSection.Link
          label={i18n.t(`${i18nScope}.loginQR`)}
          to={`${match.url}/login/qr${search}`}
          analyticsEventGroup={'sign in screen'}
          analyticsEventName={'сlick login via рс or web'}
        />
      </PhoneForm>
      <StyledBlock>
        <ActionsSection style={{ marginTop: `${scaleSize(120)}px` }}>
          <ActionsSection.Link
            label={i18n.t(`${i18nScope}.terms`)}
            to="/profile/terms"
            analyticsEventGroup={'sign in screen'}
            analyticsEventName={'click public agreement'}
          />
        </ActionsSection>
      </StyledBlock>
      <StyledLegalText noPadding>{i18n.t(`${i18nScope}.termsWarning`)}</StyledLegalText>
    </>
  );
};

AuthorizationMainPage.propTypes = {
  match: PropTypes.object.isRequired,
};

export default AuthorizationMainPage;
